'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Copy, CheckCheck, Mail, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const demoCredentials = [
  { role: 'Administrador General', email: 'admin.general@economia.gob.mx', password: 'PNE2026#Admin' },
  { role: 'Admin. de Categoría', email: 'admin.cat01@economia.gob.mx', password: 'PNE2026#Cat01' },
  { role: 'Coordinador de Grupo', email: 'coord.grupoa@economia.gob.mx', password: 'PNE2026#Coord' },
  { role: 'Juez Evaluador', email: 'juez.torres@economia.gob.mx', password: 'PNE2026#Juez1' },
  { role: 'Empresa Participante', email: 'contacto@exportnorte.com.mx', password: 'PNE2026#Emp01' },
  { role: 'Consulta / Auditoría', email: 'auditoria@economia.gob.mx', password: 'PNE2026#Audit' },
];

export default function LoginForm() {
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const router = useRouter();
  const { signIn } = useAuth();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const fillCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
    toast.success('Credenciales cargadas → haz clic en Iniciar Sesión');
  };

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);

    try {
      const result = await signIn(
        data.email,
        data.password,
        data.remember
      );

      toast.success('Sesión iniciada correctamente');

      const role = result?.user?.role;

      if (role === 'empresa') {
        router.replace('/company-questionnaire');
        return;
      }

      if (role === 'juez' || role === 'coordinador') {
        router.replace('/evaluations');
        return;
      }

      router.replace('/dashboard');
    } catch (error: any) {
      const msg = error?.message || '';

      if (msg.includes('Correo o contraseña incorrectos')) {
        toast.error(
          'Correo o contraseña incorrectos. Verifica tus credenciales.'
        );
      } else if (msg.includes('desactivada')) {
        toast.error('Esta cuenta se encuentra desactivada.');
      } else {
        toast.error(
          'Error al iniciar sesión: ' +
          (msg || 'Error desconocido')
        );
      }

      setLoading(false);
    }
  };
  const handleForgot = async () => {
    if (!forgotEmail) { toast.error('Ingresa tu correo electrónico'); return; }
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      if (error) throw error;
      setForgotSent(true);
      toast.success('Instrucciones enviadas a tu correo');
    } catch (error: any) {
      toast.error('Error al enviar instrucciones: ' + (error?.message || ''));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] institution-gradient flex-col justify-between p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute bottom-20 -left-16 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute top-1/2 right-8 w-40 h-40 rounded-full bg-secondary/10 pointer-events-none" />

        {/* Logo + brand */}
        <div className="relative">
          <div className="flex items-center gap-4 mb-10">
            <AppLogo size={52} />
            <div>
              <p className="text-white font-800 text-xl leading-tight">Premio Nacional</p>
              <p className="text-white/70 text-base">de Exportación · México</p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/20 border border-secondary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
            <span className="text-secondary text-sm font-semibold">Edición 2026 · En curso</span>
          </div>
          <h1 className="text-white font-800 text-4xl leading-tight mb-4">
            Plataforma de<br />Evaluación Oficial
          </h1>
          <p className="text-white/65 text-base leading-relaxed max-w-md">
            Sistema integral para el registro, evaluación ciega y certificación de empresas exportadoras mexicanas. Administrado por la Secretaría de Economía.
          </p>
        </div>

        {/* Stats */}
        <div className="relative grid grid-cols-3 gap-4">
          {[
            { label: 'Empresas Registradas', value: '142' },
            { label: 'Categorías Activas', value: '14' },
            { label: 'Jueces Activos', value: '89' },
          ].map((s) => (
            <div key={`stat-${s.label}`} className="bg-white/10 rounded-xl p-4 border border-white/15">
              <p className="text-white font-800 text-2xl font-tabular">{s.value}</p>
              <p className="text-white/60 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Legal */}
        <div className="relative mt-8">
          <p className="text-white/35 text-xs leading-relaxed">
            Sistema oficial del Premio Nacional de Exportación · Secretaría de Economía · México<br />
            El acceso no autorizado está prohibido y puede constituir un delito federal conforme a la LFPDPPP y el CPEUM.
          </p>
        </div>
      </div>

      {/* Right panel → form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-16 xl:px-20 bg-background overflow-y-auto">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-3 mb-8">
          <AppLogo size={40} />
          <div>
            <p className="font-800 text-base text-foreground">Premio Nacional de Exportación</p>
            <p className="text-xs text-muted-foreground">Plataforma de Evaluación · México</p>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto">
          {!showForgot ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-800 text-foreground">Iniciar sesión</h2>
                <p className="text-muted-foreground text-sm mt-1.5">
                  Accede con tus credenciales institucionales asignadas.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                {/* Email */}
                <div>
                  <label className="form-label" htmlFor="email">
                    Correo electrónico institucional
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="usuario@economia.gob.mx"
                      className={`form-input pl-10 ${errors.email ? 'error' : ''}`}
                      {...register('email', {
                        required: 'El correo electrónico es obligatorio',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ingresa un correo válido' },
                      })}
                    />
                  </div>
                  {errors.email && <p className="form-error">{errors.email.message}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className="form-label" htmlFor="password">Contraseña</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="password"
                      type={showPass ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="………………………………"
                      className={`form-input pl-10 pr-10 ${errors.password ? 'error' : ''}`}
                      {...register('password', {
                        required: 'La contraseña es obligatoria',
                        minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="form-error">{errors.password.message}</p>}
                </div>

                {/* Remember + forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-border text-primary accent-primary"
                      {...register('remember')}
                    />
                    <span className="text-sm text-muted-foreground">Recordar sesión</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-sm text-primary font-semibold hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary rounded-xl py-3 font-700 text-base flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ minHeight: 48 }}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Verificando credenciales…
                    </>
                  ) : (
                    <>
                      <LogIn size={18} />
                      Iniciar Sesión
                    </>
                  )}
                </button>
              </form>

            </>
          ) : (
            /* Forgot password panel */
            <div>
              <button
                type="button"
                onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
              >
                … Volver al inicio de sesión
              </button>
              <h2 className="text-2xl font-800 text-foreground mb-2">Recuperar contraseña</h2>
              {!forgotSent ? (
                <>
                  <p className="text-muted-foreground text-sm mb-6">
                    Ingresa tu correo institucional y te enviaremos instrucciones para restablecer tu contraseña.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label" htmlFor="forgot-email">Correo electrónico</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                          id="forgot-email"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="usuario@economia.gob.mx"
                          className="form-input pl-10"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleForgot}
                      className="w-full btn-primary rounded-xl py-3 font-700 text-base"
                    >
                      Enviar instrucciones
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <Mail size={28} className="text-success" />
                  </div>
                  <p className="text-foreground font-700 mb-2">Instrucciones enviadas</p>
                  <p className="text-muted-foreground text-sm">
                    Revisa tu bandeja de entrada en <strong>{forgotEmail}</strong> y sigue las instrucciones para restablecer tu contraseña.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
